/* =====================================================================
   SUSU — society search
   ---------------------------------------------------------------------
   Client-side filter over the collection cards already rendered by
   Horizon's Collection list section. Typing narrows the visible societies.

   Paired with the "Search societies" Custom Liquid block on the
   list-collections template. See docs/society-search.md.

   It filters what is on the page — it is not a search of the whole store.
   With ~100+ societies that is fine because the list page renders them all;
   if the list is ever paginated this needs revisiting.
   ===================================================================== */
(() => {
  'use strict';

  function normalise(value) {
    return (value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .trim();
  }

  /* If the block sits inside Horizon's Collection list section, use that
     section. Otherwise take the one holding the most collection cards. */
  function findCollectionSection(filter) {
    const parentSection = filter.closest('.shopify-section.ui-test-collection-list');
    if (parentSection && parentSection.querySelector('.collection-card__link')) {
      return parentSection;
    }

    const candidates = [
      ...document.querySelectorAll('main .shopify-section.ui-test-collection-list')
    ];
    if (!candidates.length) return null;

    candidates.sort((a, b) =>
      b.querySelectorAll('.collection-card__link').length -
      a.querySelectorAll('.collection-card__link').length
    );
    return candidates[0];
  }

  function initialise(filter) {
    if (!filter || filter.dataset.initialised === 'true') return;

    const input = filter.querySelector('[data-collection-filter-input]');
    const status = filter.querySelector('[data-collection-filter-status]');
    if (!input) return;

    const collectionSection = findCollectionSection(filter);
    if (!collectionSection) {
      console.warn('SUSU society search: Horizon collection list section was not found.');
      return;
    }

    /* Horizon puts the collection title in .collection-card__link
       .visually-hidden, so we filter on the real title rather than any other
       text that happens to be on the card. */
    const cardItems = [...collectionSection.querySelectorAll('.resource-list__item')]
      .map((item) => {
        const link = item.querySelector('.collection-card__link');
        const titleElement = link ? link.querySelector('.visually-hidden') : null;
        if (!link || !titleElement) return null;
        return {
          item: item,
          link: link,
          title: normalise(titleElement.textContent),
          url: link.getAttribute('href') || ''
        };
      })
      .filter(Boolean);

    if (!cardItems.length) return;

    /* Horizon renders duplicate cards when a mobile carousel is enabled, so
       count URLs rather than DOM nodes or the total reads high. */
    const totalCollections = new Set(cardItems.map((card) => card.url)).size;

    function setCardVisibility(card, visible) {
      /* In carousel layouts each item is wrapped in a slideshow-slide. Hide
         the slide itself, or it leaves an empty gap in the carousel. */
      const carouselSlide = card.item.closest('slideshow-slide');
      (carouselSlide || card.item).hidden = !visible;
    }

    function updateFilter() {
      const query = normalise(input.value);
      const matchedUrls = new Set();

      cardItems.forEach((card) => {
        const matches = !query || card.title.includes(query);
        setCardVisibility(card, matches);
        if (matches) matchedUrls.add(card.url);
      });

      if (!status) return;

      if (!query) {
        status.textContent = '';
        return;
      }

      const count = matchedUrls.size;
      status.textContent =
        count === 0 ? 'No collections found.'
          : count === 1 ? '1 collection found.'
            : count + ' collections found.';
    }

    input.addEventListener('input', updateFilter);
    /* The search input's native clear button fires 'search', not 'input'. */
    input.addEventListener('search', updateFilter);

    filter.dataset.initialised = 'true';
    /* Available to assistive tech without printing a count before searching. */
    filter.dataset.collectionCount = totalCollections;

    updateFilter();
  }

  function initialiseAll() {
    document.querySelectorAll('[data-collection-filter]').forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  /* Reinitialise when Shopify reloads a section in the theme editor — the
     cards are new elements, so the cached list would be stale. */
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-collection-filter]').forEach((filter) => {
      delete filter.dataset.initialised;
    });
    initialiseAll();
  });
})();
